import { useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Linkedin, Building2, UserPlus, Radio, Lock, X, Archive } from "lucide-react";
import type { HiringContact } from "../api/HiringContactsApi";
import TechTagChip from "shared/TechTagChip";
import StatusNotePopover from "shared/StatusNotePopover";
import { useHiringContacts } from "../modal/HiringContactsContext";

export const STATUS_CHIP: Record<string, { label: string; className: string }> = {
  new:             { label: "New",             className: "bg-blue-500/30 text-blue-200 border-blue-400/50" },
  engaging:        { label: "Engaging",        className: "bg-fuchsia-500/30 text-fuchsia-200 border-fuchsia-400/50" },
  connection_sent: { label: "Connection Sent", className: "bg-violet-500/30 text-violet-200 border-violet-400/50" },
  connected:       { label: "Connected",       className: "bg-sky-500/30 text-sky-200 border-sky-400/50" },
  messaged:        { label: "Messaged",        className: "bg-amber-500/35 text-amber-200 border-amber-400/50" },
  responded:       { label: "Responded",       className: "bg-teal-500/35 text-teal-200 border-teal-400/50" },
  converted:       { label: "Converted",       className: "bg-emerald-500 text-emerald-950 border-emerald-400" },
  not_relevant:    { label: "Not Relevant",    className: "bg-zinc-600/50 text-zinc-300 border-zinc-500/50" },
  archived:        { label: "Archived",        className: "bg-zinc-800/70 text-zinc-400 border-zinc-600/50" },
};

type Priority = "high" | "medium" | "low";

export const PRIORITY_STYLES: Record<Priority, string> = {
  high:   "bg-red-500/15 text-red-300 border-red-500/25 hover:bg-red-500/25",
  medium: "bg-amber-500/15 text-amber-300 border-amber-500/25 hover:bg-amber-500/25",
  low:    "bg-slate-700/50 text-slate-400 border-slate-600/30 hover:bg-slate-700/70",
};
export const PRIORITY_LABELS: Record<Priority, string> = { high: "High", medium: "Medium", low: "Low" };
const PRIORITY_CYCLE: (Priority | null)[] = [null, "high", "medium", "low"];

/** Admin-only triage tag — cycles None → High → Medium → Low → None on click.
 * Never shown to TMs: the retired Watchlist Profiles tab this replaces was
 * entirely admin-facing, so this preserves that scope rather than introducing a
 * new employee-visible signal. */
export function PriorityBadge({ priority, onChange }: { priority?: Priority | null; onChange: (next: Priority | null) => void }) {
  const { isAdmin } = useHiringContacts();
  if (!isAdmin) return null;

  function cycle() {
    const idx = PRIORITY_CYCLE.indexOf(priority ?? null);
    onChange(PRIORITY_CYCLE[(idx + 1) % PRIORITY_CYCLE.length]);
  }

  return (
    <button
      onClick={cycle}
      title="Click to change triage priority"
      className={`shrink-0 text-[10px] px-1.5 py-0.5 rounded border font-inter font-medium transition-colors ${
        priority ? PRIORITY_STYLES[priority] : "bg-surface-muted text-muted-foreground border-border hover:border-border hover:text-foreground"
      }`}
    >
      {priority ? PRIORITY_LABELS[priority] : "+ Priority"}
    </button>
  );
}

/** Small, distinctly-shaped (circular, not a pill) toggle so "LinkedIn monitoring"
 * never reads as another outreach-status option — different concept, different shape.
 *
 * Only admins may flip this — regular TMs still see the accurate watched/not-watched
 * state, but clicking it shows a "contact admin" notice instead of calling onToggle,
 * since the backend has no employee-facing watch endpoint anyway. */
export function WatchToggle({ isWatched, onToggle, disabled }: { isWatched: boolean; onToggle: () => void; disabled?: boolean }) {
  const { isAdmin } = useHiringContacts();
  const btnRef = useRef<HTMLButtonElement>(null);
  const [showRestricted, setShowRestricted] = useState(false);
  const [rect, setRect] = useState<DOMRect | null>(null);

  function handleClick() {
    if (!isAdmin) {
      if (btnRef.current) setRect(btnRef.current.getBoundingClientRect());
      setShowRestricted(true);
      return;
    }
    onToggle();
  }

  return (
    <>
      <button
        ref={btnRef}
        onClick={handleClick}
        disabled={disabled}
        title={
          disabled
            ? "Add a LinkedIn URL before watching"
            : isWatched
            ? isAdmin ? "Watching for new LinkedIn posts — click to stop" : "Watching for new LinkedIn posts"
            : isAdmin ? "Not watching — click to monitor their LinkedIn posts" : "Not watching"
        }
        className={`shrink-0 w-6 h-6 rounded-full border flex items-center justify-center transition-colors disabled:opacity-30 disabled:cursor-not-allowed ${
          isWatched
            ? "bg-emerald-500/20 border-emerald-500/50 text-emerald-300 hover:bg-emerald-500/30"
            : "bg-surface-muted border-border text-muted-foreground hover:border-border hover:text-foreground"
        }`}
      >
        <Radio className="w-3 h-3" />
      </button>

      {showRestricted && rect && createPortal(
        <>
          <div className="fixed inset-0 z-[80]" onClick={() => setShowRestricted(false)} />
          <div
            className="fixed z-[81] w-60 bg-surface-muted border border-border rounded-xl shadow-2xl p-3"
            style={{ top: rect.bottom + 6, left: Math.min(Math.max(8, rect.left - 90), window.innerWidth - 248) }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-start gap-2">
                <Lock className="w-3.5 h-3.5 text-amber-400 shrink-0 mt-0.5" />
                <p className="text-[11px] text-foreground font-inter leading-relaxed">
                  Only an admin can change LinkedIn monitoring. Contact your admin if this needs to change.
                </p>
              </div>
              <button
                onClick={() => setShowRestricted(false)}
                className="p-0.5 text-muted-foreground hover:text-foreground rounded transition-colors shrink-0"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </>,
        document.body,
      )}
    </>
  );
}

interface Props {
  contact: HiringContact;
  onAssign?: () => void;
  /** Employee's own "My Contacts" view only — a contact is assigned to exactly one
   * TM there, so the whole card can open that single tracking modal directly. */
  onClick?: () => void;
  /** Admin view — a contact can carry multiple independent TM assignments at once,
   * so each one gets its own clickable "name · status" chip instead of a single
   * card-level click; this opens the tracking modal for that specific assignment. */
  onSelectAssignment?: (assignmentId: number) => void;
}

export default function ContactCard({ contact, onAssign, onClick, onSelectAssignment }: Props) {
  const { handleContactWatchToggle, handleContactPriorityChange, handleArchiveContact, isAdmin } = useHiringContacts();

  // contact.status is only present on items loaded from an /assignments endpoint —
  // plain /contacts entries (e.g. the Contacts tab's "Unassigned" view) carry no
  // status at all, so don't default to "new" here or every contact would misleadingly
  // show a badge. Only shown for the employee's own single-assignment view — the
  // admin view shows status per-assignee in the chip row below instead, since a
  // single top badge can't represent multiple independent assignments.
  const status = contact.status;
  const statusEntry = !isAdmin && status ? STATUS_CHIP[status] : null;

  const assignedEmployees = isAdmin ? contact.assigned_employees ?? [] : [];

  return (
    <div
      onClick={onClick}
      className={`bg-surface/60 border border-border/60 rounded-2xl p-4 ${
        onClick ? "cursor-pointer hover:border-border transition-colors select-none" : ""
      }`}
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full border border-border bg-surface-muted flex items-center justify-center shrink-0 overflow-hidden">
          {contact.thumbnail_url ? (
            <img src={contact.thumbnail_url} alt={contact.full_name} className="w-full h-full object-cover" />
          ) : (
            <span className="text-sm font-bold text-primary font-inter">
              {contact.full_name.charAt(0).toUpperCase()}
            </span>
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <h3 className="text-sm font-semibold text-foreground font-inter leading-snug">
              <a
                href={contact.linkedin_url}
                target="_blank"
                rel="noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="hover:underline inline-flex items-center gap-1"
              >
                {contact.full_name}
                <Linkedin className="w-3 h-3 text-sky-400" />
              </a>
            </h3>
            <div className="flex items-center gap-1.5 shrink-0" onClick={(e) => e.stopPropagation()}>
              <PriorityBadge priority={contact.priority} onChange={(p) => handleContactPriorityChange(contact.id, p)} />
              <WatchToggle isWatched={contact.is_watched} onToggle={() => handleContactWatchToggle(contact.id, !contact.is_watched)} />
              {statusEntry && (
                <StatusNotePopover
                  note={contact.assignment_notes}
                  fromLabel={contact.assignment_from_status ? STATUS_CHIP[contact.assignment_from_status]?.label : null}
                  toLabel={statusEntry.label}
                >
                  <span className={`shrink-0 text-[10px] px-1.5 py-0.5 rounded border font-inter font-medium ${statusEntry.className}`}>
                    {statusEntry.label}
                  </span>
                </StatusNotePopover>
              )}
            </div>
          </div>

          {contact.role && <p className="text-xs text-muted-foreground font-inter mt-0.5">{contact.role}</p>}

          {contact.company && (
            <div className="flex items-center gap-1.5 mt-0.5 min-w-0">
              <Building2 className="w-3 h-3 text-muted-foreground shrink-0" />
              <span className="text-xs text-muted-foreground font-inter truncate">{contact.company.name}</span>
              {contact.company.linkedin_url && (
                <a
                  href={contact.company.linkedin_url}
                  target="_blank"
                  rel="noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="text-muted-foreground hover:text-sky-400 shrink-0"
                >
                  <Linkedin className="w-3 h-3" />
                </a>
              )}
            </div>
          )}

          {contact.tech_tags.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {contact.tech_tags.slice(0, 6).map((tag) => (
                <TechTagChip key={tag} label={tag} />
              ))}
            </div>
          )}

          {(assignedEmployees.length > 0 || onAssign) && (
            <div className="flex flex-wrap items-center gap-1.5 mt-2" onClick={(e) => e.stopPropagation()}>
              {assignedEmployees.map((a) => {
                const chip = STATUS_CHIP[a.status];
                return (
                  <button
                    key={a.assignment_id}
                    onClick={() => onSelectAssignment?.(a.assignment_id)}
                    className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full border font-inter font-medium transition-colors ${
                      onSelectAssignment ? "hover:brightness-110 cursor-pointer" : "cursor-default"
                    } ${chip?.className ?? "bg-surface-muted border-border text-foreground"}`}
                  >
                    {a.name}
                    <span className="opacity-70">· {chip?.label ?? a.status}</span>
                  </button>
                );
              })}
              {onAssign && (
                <>
                  <button
                    onClick={() => handleArchiveContact(contact.id)}
                    className="ml-auto flex items-center gap-1 text-[11px] text-muted-foreground hover:text-destructive bg-surface-muted hover:bg-destructive/10 border border-border hover:border-destructive/30 rounded-lg px-2 py-1 transition-colors font-inter"
                  >
                    <Archive className="w-3 h-3" />
                    Archive
                  </button>
                  <button
                    onClick={onAssign}
                    className="flex items-center gap-1 text-[11px] text-primary hover:text-primary-foreground bg-primary/20 hover:bg-primary border border-primary/30 hover:border-primary rounded-lg px-2 py-1 transition-colors font-inter"
                  >
                    <UserPlus className="w-3 h-3" />
                    Assign
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
